import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useEnvironment } from '../context/EnvironmentContext';
import { useLayoutVariant } from '../context/LayoutVariantContext';
import { useNotifications } from '../hooks/useNotifications';
import { toast } from '../lib/safeToast';
import {
  ArrowRight,
  ArrowLeft,
  Loader2,
  RefreshCw,
  SendHorizontal,
  Zap,
  Lock,
  Banknote,
  Globe,
  CreditCard,
  FileText,
  ChevronRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { getApiErrorMessage } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { cn } from '../lib/utils';

// Wise-style step labels (clone Wise: Amount | Recipient | Review | Pay)
const WISE_STEPS = [
  { id: 1, label: 'Amount' },
  { id: 2, label: 'Rate' },
  { id: 3, label: 'Recipient' },
  { id: 4, label: 'Pay' },
];
const STEPS = [
  { id: 1, title: 'Amount & currency', description: 'Choose amount and currencies' },
  { id: 2, title: 'Live quote', description: 'Get FX rate and total' },
  { id: 3, title: 'Recipient', description: 'Who to send to' },
  { id: 4, title: 'Confirm & send', description: 'Review and complete' },
];

const CURRENCIES = ['AUD', 'USD', 'EUR', 'GBP', 'SGD', 'NZD', 'JPY', 'CHF', 'IDR', 'INR', 'PHP'];

// Recipient country (destination) for Australia → other country transfers. 2-letter codes for PIN API.
const RECIPIENT_COUNTRIES = [
  { code: 'AU', name: 'Australia' },
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'SG', name: 'Singapore' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'JP', name: 'Japan' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'IN', name: 'India' },
  { code: 'PH', name: 'Philippines' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'CN', name: 'China' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'CA', name: 'Canada' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'TH', name: 'Thailand' },
  { code: 'VN', name: 'Vietnam' },
];

const MoneyTransfer = () => {
  const { api, user } = useAuth();
  const { isLive } = useEnvironment();
  const { isReference } = useLayoutVariant();
  const { fetchNotifications } = useNotifications();
  const kycBlocked = isLive && user?.kyc_status !== 'approved';

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [fxLoading, setFxLoading] = useState(false);

  const [fromCurrency, setFromCurrency] = useState('AUD');
  const [toCurrency, setToCurrency] = useState('USD');
  const [amount, setAmount] = useState('');
  const [fxRate, setFxRate] = useState(null);
  const [quoteTime, setQuoteTime] = useState(null);
  const [recipient, setRecipient] = useState({
    name: '',
    account: '',
    bsb: '',
    country: 'AU',
    reference: '',
  });
  const [beneficiaries, setBeneficiaries] = useState([]);

  const pageTitleClass = isReference ? 'text-3xl font-bold text-[#073B2E] tracking-tight' : 'text-[2.25rem] font-bold leading-tight tracking-tight text-[#0F172A]';
  const pageSubtitleClass = isReference ? 'text-[#0E1B16] opacity-70 mt-1' : 'text-base text-slate-500 mt-1';
  const cardClass = isReference ? 'border-[#DCE4E0] shadow-sm rounded-xl bg-white' : 'card';
  const sectionTitleClass = isReference ? 'text-xl font-bold text-[#073B2E] mb-4' : 'text-xl font-bold text-[#0F172A] mb-4';

  useEffect(() => {
    const loadBeneficiaries = async () => {
      try {
        const res = await api.get('/beneficiaries');
        setBeneficiaries(res.data?.data || []);
      } catch {
        setBeneficiaries([]);
      }
    };
    loadBeneficiaries();
  }, [api]);

  const fetchLiveQuote = async () => {
    const num = parseFloat(amount);
    if (!amount || isNaN(num) || num <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    if (fromCurrency === toCurrency) {
      setFxRate(1);
      setQuoteTime('Same currency');
      return;
    }
    setFxLoading(true);
    try {
      const res = await api.get(`/fx/rate?from=${fromCurrency}&to=${toCurrency}`);
      const rate = res.data?.rate;
      if (rate != null) {
        setFxRate(Number(rate));
        setQuoteTime(res.data?.source === 'live' ? 'Live rate' : 'Approximate');
      } else {
        setFxRate(fromCurrency === 'AUD' && toCurrency === 'USD' ? 0.65 : fromCurrency === 'USD' && toCurrency === 'AUD' ? 1.54 : 1);
        setQuoteTime('Approximate');
      }
    } catch (_) {
      setFxRate(fromCurrency === 'AUD' && toCurrency === 'USD' ? 0.65 : fromCurrency === 'USD' && toCurrency === 'AUD' ? 1.54 : 1);
      setQuoteTime('Approximate');
    } finally {
      setFxLoading(false);
    }
  };

  // Step 1: when currencies differ, fetch rate so "Beneficiary receives" auto-updates as user types amount
  useEffect(() => {
    if (fromCurrency === toCurrency) {
      setFxRate(1);
      setQuoteTime('Same currency');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/fx/rate?from=${fromCurrency}&to=${toCurrency}`);
        if (cancelled) return;
        const rate = res.data?.rate;
        if (rate != null) {
          setFxRate(Number(rate));
          setQuoteTime(res.data?.source === 'live' ? 'Live rate' : 'Approximate');
        } else {
          setFxRate(fromCurrency === 'AUD' && toCurrency === 'USD' ? 0.65 : fromCurrency === 'USD' && toCurrency === 'AUD' ? 1.54 : 1);
          setQuoteTime('Approximate');
        }
      } catch (_) {
        if (!cancelled) {
          setFxRate(fromCurrency === 'AUD' && toCurrency === 'USD' ? 0.65 : fromCurrency === 'USD' && toCurrency === 'AUD' ? 1.54 : 1);
          setQuoteTime('Approximate');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [fromCurrency, toCurrency, api]);

  useEffect(() => {
    if (step === 2 && amount && fromCurrency !== toCurrency && !fxRate) {
      fetchLiveQuote();
    }
  }, [step, amount, fromCurrency, toCurrency]);

  const convertedAmount = fxRate != null && amount ? parseFloat(amount) * fxRate : null;

  const canProceedStep1 = amount && parseFloat(amount) > 0 && fromCurrency && toCurrency;
  const canProceedStep2 = (fromCurrency === toCurrency && parseFloat(amount) > 0) || (fxRate != null && convertedAmount != null);
  const canProceedStep3 = recipient.name?.trim() && recipient.account?.trim() &&
    (recipient.country !== 'AU' || (recipient.bsb?.replace(/\D/g, '').length === 6));

  const handleSubmitTransfer = async () => {
    setLoading(true);
    try {
      await api.post('/banking/payments', {
        amount: fromCurrency === 'AUD' ? parseFloat(amount) : convertedAmount,
        currency: toCurrency,
        account: recipient.account.trim(),
        name: recipient.name.trim(),
        bsb: recipient.bsb || undefined,
        country: recipient.country || 'AU',
        reference: recipient.reference || `Transfer ${amount} ${fromCurrency} → ${toCurrency}`,
      });
      toast.success('Transfer submitted successfully');
      setStep(1);
      setAmount('');
      setFxRate(null);
      setRecipient({ name: '', account: '', bsb: '', country: 'AU', reference: '' });
      fetchNotifications();
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Complete KYC to make transfers.');
      } else {
        toast.error(getApiErrorMessage(err.response?.data?.detail, 'Transfer failed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const showReferenceStep1 = step === 1;

  return (
    <Layout>
      <div data-testid="money-transfer-page" className={cn('-mt-2', showReferenceStep1 ? 'space-y-10' : 'space-y-6')}>
        <div>
          <h1 className={cn('text-2xl font-bold text-[#073B2E]', !showReferenceStep1 && pageTitleClass)}>
            {showReferenceStep1 ? 'Initiate cross-border business payment with competitive rates' : 'Money Transfer'}
          </h1>
          {showReferenceStep1 && fromCurrency === 'AUD' && (
            <>
              <p className="text-sm text-slate-600 mt-1">Send from Australia to 150+ countries. Competitive rates, no hidden fees.</p>
              <p className="text-xs text-slate-400 mt-1" aria-hidden="true">Step 1 of 4</p>
            </>
          )}
          {!showReferenceStep1 && <p className={pageSubtitleClass}>{STEPS.find((s) => s.id === step)?.description}</p>}
        </div>

        {kycBlocked && (
          <div className="rounded-2xl border-2 border-amber-400 bg-amber-50/50 p-6">
            <p className="text-[#0F172A] font-medium">Complete KYC verification to use Money Transfer.</p>
          </div>
        )}

        {!showReferenceStep1 && (
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <nav className="flex items-center gap-6" aria-label="Transfer steps">
              {WISE_STEPS.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => !kycBlocked && setStep(s.id)}
                  className={cn(
                    'pb-2 text-sm font-medium transition-colors border-b-2 -mb-px',
                    step === s.id ? 'text-[#0B6445] border-[#0B6445]' : 'text-slate-500 border-transparent hover:text-slate-700'
                  )}
                >
                  {s.label}
                </button>
              ))}
            </nav>
            {step === 2 && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 text-slate-600 text-sm">
                <Lock className="w-3.5 h-3.5" />
                <span>Guaranteed for 72h</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </div>
            )}
          </div>
        )}

        <div className={cn('grid grid-cols-1 lg:grid-cols-3 gap-8', !showReferenceStep1 && 'items-start')}>
          <div className="lg:col-span-2">
        {showReferenceStep1 ? (
              <Card className="rounded-xl shadow-sm border-[#DCE4E0] bg-white">
                <CardHeader className="px-6 pt-6 pb-2">
                  <CardTitle className="text-lg font-bold text-[#073B2E]">Transfer Details</CardTitle>
                </CardHeader>
                <CardContent className="px-6 pt-2 pb-6 space-y-5">
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-2 block">You Send</Label>
                    <div className="flex gap-3">
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="1000"
                        className="input-field h-12 flex-1 rounded-lg border-slate-200 bg-white"
                      />
                      <Select value={fromCurrency} onValueChange={setFromCurrency}>
                        <SelectTrigger className="w-32 h-12 rounded-lg border-slate-200"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {CURRENCIES.map((c) => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-2 block">Beneficiary Receives</Label>
                    <div className="flex gap-3">
                      <Input
                        type="text"
                        readOnly
                        value={convertedAmount != null && amount ? convertedAmount.toFixed(2) : '0'}
                        className="input-field h-12 flex-1 rounded-lg bg-slate-50 border-slate-200"
                      />
                      <Select value={toCurrency} onValueChange={setToCurrency}>
                        <SelectTrigger className="w-32 h-12 rounded-lg border-slate-200"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {CURRENCIES.map((c) => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {amount && (
                      <button
                        type="button"
                        onClick={fetchLiveQuote}
                        disabled={fxLoading}
                        className="mt-2 text-sm text-[#073B2E] font-medium hover:underline"
                      >
                        {fxLoading ? 'Getting rate...' : 'Get live rate'}
                      </button>
                    )}
                  </div>
                  {(amount && parseFloat(amount) > 0) && (
                    <div className="rounded-lg border border-[#DCE4E0] bg-[#F7FAF9] px-4 py-3 space-y-2 text-sm">
                      {fromCurrency !== toCurrency && fxRate != null && (
                        <p className="text-slate-600">
                          <span className="font-medium text-[#073B2E]">Rate:</span> 1 {fromCurrency} = {fxRate.toFixed(4)} {toCurrency}
                          {quoteTime && <span className="text-slate-500"> · {quoteTime}</span>}
                        </p>
                      )}
                      {convertedAmount != null && (
                        <p className="text-slate-600">
                          <span className="font-medium text-[#073B2E]">Recipient receives:</span> {convertedAmount.toFixed(2)} {toCurrency}
                        </p>
                      )}
                      <p className="text-slate-500 text-xs">Fees included in rate. No hidden fees.</p>
                    </div>
                  )}
                  <Button
                    className="w-full h-12 rounded-lg bg-[#6B9E8E] hover:bg-[#5a8d7d] text-white font-semibold"
                    disabled={!canProceedStep1}
                    onClick={() => setStep(2)}
                  >
                    Continue Transfer <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
        ) : (
          <>
            {/* Step 2 – aligned with step 1: boxed sections, same spacing and buttons */}
            {step === 2 && (
              <Card className="rounded-xl shadow-sm border-[#DCE4E0] bg-white">
                <CardHeader className="px-6 pt-6 pb-2">
                  <CardTitle className="text-lg font-bold text-[#073B2E]">{STEPS.find((s) => s.id === 2)?.title}</CardTitle>
                  <p className="text-sm text-slate-500 mt-0.5">{STEPS.find((s) => s.id === 2)?.description}</p>
                </CardHeader>
                <CardContent className="px-6 pt-2 pb-6 space-y-5">
                {fxLoading ? (
                  <div className="flex items-center gap-2 text-slate-500 py-8">
                    <Loader2 className="w-5 h-5 animate-spin shrink-0" />
                    Getting your rate…
                  </div>
                ) : (
                  <>
                    <div className="rounded-lg border border-[#DCE4E0] bg-[#F7FAF9] px-4 py-4 space-y-4">
                      <div>
                        <Label className="text-sm font-medium text-slate-700 mb-2 block">You send exactly</Label>
                        <div className="flex items-center gap-3 flex-wrap">
                          <Select value={fromCurrency} onValueChange={setFromCurrency}>
                            <SelectTrigger className="w-32 h-12 rounded-lg border-slate-200 bg-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {CURRENCIES.map((c) => (
                                <SelectItem key={c} value={c}>{c}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            className="w-32 h-12 rounded-lg border-slate-200 bg-white text-[1.5rem] font-bold text-[#073B2E] leading-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none placeholder:text-slate-400"
                            style={{ fontSize: '1.5rem' }}
                            placeholder="0"
                            title="Type to change amount"
                          />
                        </div>
                        <p className="text-sm text-slate-500 mt-1.5">
                          We will convert {amount || '0'} {fromCurrency} at current rate. Fees included in amount.
                        </p>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-slate-700 mb-2 block">Recipient gets</Label>
                        <div className="flex items-center gap-3 flex-wrap">
                          <Select value={toCurrency} onValueChange={setToCurrency}>
                            <SelectTrigger className="w-32 h-12 rounded-lg border-slate-200 bg-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {CURRENCIES.map((c) => (
                                <SelectItem key={c} value={c}>{c}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <span className="inline-flex items-center h-12 text-[1.5rem] font-bold text-[#073B2E] leading-none">
                            {convertedAmount != null ? convertedAmount.toFixed(2) : '0.00'}
                          </span>
                        </div>
                        {fromCurrency !== toCurrency && (
                          <button type="button" onClick={fetchLiveQuote} disabled={fxLoading} className="mt-2 text-sm text-[#073B2E] font-medium hover:underline flex items-center gap-1.5">
                            <RefreshCw className="w-3.5 h-3.5" /> Refresh rate
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="rounded-lg border border-[#DCE4E0] bg-white px-4 py-3 space-y-0">
                      <div className="flex items-center justify-between py-3 border-b border-slate-100">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                            <CreditCard className="w-4 h-4 text-slate-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-800">Paying with</p>
                            <p className="text-xs text-slate-500">Bank transfer</p>
                          </div>
                        </div>
                        <button type="button" className="text-sm font-medium text-[#073B2E] hover:underline">Change</button>
                      </div>
                      <div className="flex items-center justify-between py-3 border-b border-slate-100">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
                            <Zap className="w-4 h-4 text-amber-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-800">Arrives</p>
                            <p className="text-xs text-slate-500">1–2 business days</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                            <FileText className="w-4 h-4 text-slate-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-800">Estimated fees</p>
                            <p className="text-xs text-slate-500">Included in {fromCurrency} amount</p>
                          </div>
                        </div>
                        <span className="text-sm font-medium text-slate-700">0.00 {fromCurrency}</span>
                      </div>
                    </div>
                    <p className="text-sm text-slate-500">
                      By continuing, I accept the <a href="/terms" className="text-[#073B2E] font-medium underline hover:no-underline">Terms of Use</a>.
                    </p>
                    <div className="flex gap-3 pt-1">
                      <Button variant="outline" onClick={() => setStep(1)} className="h-12 rounded-lg px-6 border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900"><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
                      <Button disabled={!canProceedStep2} onClick={() => setStep(3)} className="h-12 rounded-lg bg-[#6B9E8E] hover:bg-[#5a8d7d] text-white font-semibold px-6 flex-1">Continue <ArrowRight className="w-4 h-4 ml-2" /></Button>
                    </div>
                  </>
                )}
                </CardContent>
              </Card>
            )}

            {/* Step 3 – aligned with step 1: same labels, input heights, button style */}
            {step === 3 && (
              <Card className="rounded-xl shadow-sm border-[#DCE4E0] bg-white">
                <CardHeader className="px-6 pt-6 pb-2">
                  <CardTitle className="text-lg font-bold text-[#073B2E]">{STEPS.find((s) => s.id === 3)?.title}</CardTitle>
                  <p className="text-sm text-slate-500 mt-0.5">{STEPS.find((s) => s.id === 3)?.description}</p>
                </CardHeader>
                <CardContent className="px-6 pt-2 pb-6 space-y-5">
                <div className="space-y-5">
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-2 block">Country</Label>
                    <Select value={recipient.country} onValueChange={(v) => setRecipient((r) => ({ ...r, country: v }))}>
                      <SelectTrigger className="h-12 rounded-lg border-slate-200 bg-white">
                        <SelectValue placeholder="Choose country" />
                      </SelectTrigger>
                      <SelectContent>
                        {RECIPIENT_COUNTRIES.map((c) => (
                          <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-2 block">Full name or business name</Label>
                    <Input
                      value={recipient.name}
                      onChange={(e) => setRecipient((r) => ({ ...r, name: e.target.value }))}
                      placeholder="As it appears on their account"
                      className="h-12 rounded-lg border-slate-200 bg-white"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-2 block">Account number</Label>
                    <Input
                      value={recipient.account}
                      onChange={(e) => setRecipient((r) => ({ ...r, account: e.target.value }))}
                      placeholder={recipient.country === 'AU' ? 'BSB and account number' : 'IBAN or account number'}
                      className="h-12 rounded-lg border-slate-200 font-mono bg-white"
                    />
                  </div>
                  {recipient.country === 'AU' && (
                    <div className="space-y-3">
                      <Label className="text-sm font-medium text-slate-700 block">BSB</Label>
                      <Input
                        value={recipient.bsb}
                        onChange={(e) => setRecipient((r) => ({ ...r, bsb: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                        placeholder="000-000"
                        className="h-12 rounded-lg border-slate-200 font-mono bg-white"
                        required
                      />
                    </div>
                  )}
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-2 block">Reference <span className="font-normal text-slate-400">(optional)</span></Label>
                    <Input
                      value={recipient.reference}
                      onChange={(e) => setRecipient((r) => ({ ...r, reference: e.target.value }))}
                      placeholder="Invoice or payment reference"
                      className="h-12 rounded-lg border-slate-200 bg-white"
                    />
                  </div>
                </div>
                <div className="flex gap-3 pt-1">
                  <Button variant="outline" onClick={() => setStep(2)} className="h-12 rounded-lg px-6 border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900"><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
                  <Button disabled={!canProceedStep3} onClick={() => setStep(4)} className="h-12 rounded-lg bg-[#6B9E8E] hover:bg-[#5a8d7d] text-white font-semibold px-6 flex-1">Review & send <ArrowRight className="w-4 h-4 ml-2" /></Button>
                </div>
                </CardContent>
              </Card>
            )}

            {/* Step 4 – aligned with step 1: boxed summary, same button style */}
            {step === 4 && (
              <Card className="rounded-xl shadow-sm border-[#DCE4E0] bg-white">
                <CardHeader className="px-6 pt-6 pb-2">
                  <CardTitle className="text-lg font-bold text-[#073B2E]">{STEPS.find((s) => s.id === 4)?.title}</CardTitle>
                  <p className="text-sm text-slate-500 mt-0.5">{STEPS.find((s) => s.id === 4)?.description}</p>
                </CardHeader>
                <CardContent className="px-6 pt-2 pb-6 space-y-5">
                <div className="rounded-lg border border-[#DCE4E0] bg-[#F7FAF9] px-4 py-4 space-y-4">
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">You send exactly</p>
                    <p className="text-xl font-bold text-[#073B2E]">{amount} {fromCurrency}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Recipient gets</p>
                    <p className="text-xl font-bold text-[#073B2E]">{convertedAmount != null ? convertedAmount.toFixed(2) : '—'} {toCurrency}</p>
                    {fromCurrency !== toCurrency && fxRate != null && (
                      <p className="text-sm text-slate-500 mt-1">1 {fromCurrency} = {fxRate.toFixed(4)} {toCurrency} · Fees included</p>
                    )}
                  </div>
                </div>
                <div className="rounded-lg border border-[#DCE4E0] bg-white px-4 py-3 space-y-0">
                  <div className="flex justify-between items-center py-3 border-b border-slate-100">
                    <span className="text-sm text-slate-500">To</span>
                    <span className="text-sm font-medium text-slate-800">{recipient.name || '—'}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-slate-100">
                    <span className="text-sm text-slate-500">Country</span>
                    <span className="text-sm font-medium text-slate-800">{RECIPIENT_COUNTRIES.find((c) => c.code === recipient.country)?.name || recipient.country}</span>
                  </div>
                  <div className="flex justify-between items-center py-3">
                    <span className="text-sm text-slate-500">Account</span>
                    <span className="text-sm font-mono font-medium text-slate-800">{recipient.account || '—'}</span>
                  </div>
                  {recipient.reference && (
                    <div className="flex justify-between items-center py-3 border-t border-slate-100">
                      <span className="text-sm text-slate-500">Reference</span>
                      <span className="text-sm font-medium text-slate-800">{recipient.reference}</span>
                    </div>
                  )}
                </div>
                <p className="text-sm text-slate-500">
                  By continuing, I accept the <a href="/terms" className="text-[#073B2E] font-medium underline hover:no-underline">Terms of Use</a>.
                </p>
                <div className="flex gap-3 pt-1">
                  <Button variant="outline" onClick={() => setStep(3)} className="h-12 rounded-lg px-6 border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900"><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
                  <Button
                    className="h-12 rounded-lg bg-[#6B9E8E] hover:bg-[#5a8d7d] text-white font-semibold px-6 flex-1"
                    disabled={loading || kycBlocked}
                    onClick={handleSubmitTransfer}
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <SendHorizontal className="w-4 h-4 mr-2" />}
                    Pay
                  </Button>
                </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
          </div>
          {/* Right sidebar – same on every step */}
          <div className="space-y-6">
            <Card className="rounded-xl shadow-sm border-[#DCE4E0] bg-white">
              <CardHeader className="px-6 pt-6 pb-2">
                <CardTitle className="text-lg font-bold text-[#073B2E]">Why Choose PayZen?</CardTitle>
              </CardHeader>
              <CardContent className="px-6 pt-2 pb-6 space-y-4">
                <div className="flex gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#E6F7F3] flex items-center justify-center shrink-0">
                    <Zap className="w-5 h-5 text-amber-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#0F172A] text-sm">Fast Business Payments</p>
                    <p className="text-xs text-slate-500">Business payment times vary by destination and payment method</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center shrink-0">
                    <Lock className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#0F172A] text-sm">Secure & Safe</p>
                    <p className="text-xs text-slate-500">Bank-level security, AUSTRAC registered</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center shrink-0">
                    <Banknote className="w-5 h-5 text-purple-700" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#0F172A] text-sm">Best Rates</p>
                    <p className="text-xs text-slate-500">Competitive fees, mid-market exchange rates</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                    <Globe className="w-5 h-5 text-amber-700" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#0F172A] text-sm">8 Currencies</p>
                    <p className="text-xs text-slate-500">AUD, USD, EUR, GBP, IDR, PHP, INR, SGD</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="rounded-xl shadow-sm border-[#DCE4E0] bg-white">
              <CardHeader className="px-6 pt-6 pb-2">
                <CardTitle className="text-lg font-bold text-[#073B2E]">First Transfer?</CardTitle>
              </CardHeader>
              <CardContent className="px-6 pt-2 pb-6">
                <p className="text-sm text-slate-600 mb-4">
                  Get started with our business verification process. Most business accounts are approved within business days.
                </p>
                <Link to="/onboarding">
                  <Button variant="outline" className="w-full rounded-lg border-[#073B2E] text-[#073B2E] hover:bg-[#E6F7F3] hover:text-[#073B2E]">
                    Complete Profile
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default MoneyTransfer;
